import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Heart, MessageCircle, HandHeart, BookOpen, Coins, ArrowRight, Sparkles, Eye, HeartHandshake } from "lucide-react";
import CoinCard from "@/components/CoinCard";
import { AMIR_COIN } from "@/lib/coin";

const COVER =
  "https://customer-assets.emergentagent.com/job_miracle-babies-amir/artifacts/wj6tszkr_Gemini_Generated_Image_io6e9eio6e9eio6e.png";
const HANDS =
  "https://images.unsplash.com/photo-1552819289-824d37ca69d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxiYWJ5JTIwaG9sZGluZyUyMHBhcmVudCUyMGZpbmdlciUyMHdhcm0lMjBsaWdodHxlbnwwfHx8fDE3ODMyMDYwNzZ8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: MessageCircle, title: "Community Forum", desc: "Share, ask, and connect with parents who truly understand.", to: "/forum", tint: "bg-[#6B8EAC]" },
  { icon: HandHeart, title: "Help Board", desc: "Request or offer support, thanked with AMIR tokens.", to: "/help", tint: "bg-[#D4AF37]" },
  { icon: Sparkles, title: "Miracle Gallery", desc: "Art, murals and joyful milestones shared by families.", to: "/gallery", tint: "bg-[#6B8EAC]" },
  { icon: BookOpen, title: "Resources", desc: "Trusted info on NICU, trach and g-tube care.", to: "/resources", tint: "bg-[#D4AF37]" },
];

function galleryImg(item) {
  const url = item.image_url || "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Home() {
  const [stats, setStats] = useState(null);
  const [art, setArt] = useState([]);
  const [artIdx, setArtIdx] = useState(0);
  const [visits, setVisits] = useState(null);
  const [support, setSupport] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setArt(r.data.filter((i) => i.image_url))).catch(() => {});
    api.get("/visits").then((r) => setVisits(r.data.count)).catch(() => {});
    api.get("/support-messages").then((r) => setSupport(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const shared = stats?.amir_shared?.[0]?.total || 0;

  const VISIBLE = 3;
  useEffect(() => {
    if (art.length <= VISIBLE) return;
    const t = setInterval(() => setArtIdx((i) => (i + 1) % art.length), 4000);
    return () => clearInterval(t);
  }, [art.length]);

  const visibleArt =
    art.length <= VISIBLE
      ? art
      : Array.from({ length: VISIBLE }, (_, k) => art[(artIdx + k) % art.length]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Subtle hands accent in the background */}
        <img
          src={HANDS}
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute -top-10 -left-16 w-[480px] md:w-[640px] opacity-[0.20] rotate-[-8deg] rounded-[3rem] object-cover"
        />
        <img
          src={HANDS}
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute bottom-0 right-0 w-[320px] md:w-[420px] opacity-[0.12] rotate-[10deg] rounded-[3rem] object-cover hidden md:block"
        />
        <div className="absolute top-1/3 right-0 w-72 h-72 bg-[#6A0DAD]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <a
              href={AMIR_COIN.pumpUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-coin-badge"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#6A0DAD]/10 text-[#6A0DAD] text-xs font-heading font-semibold tracking-wide uppercase hover:bg-[#6A0DAD]/20 transition-colors"
            >
              <Coins size={14} /> AMIR live on Solana — trade on pump.fun
            </a>
            <div className="mt-5">
              <p className="font-display text-4xl sm:text-5xl text-[#6A0DAD] tracking-[0.15em] font-semibold">A.M.I.R</p>
              <p className="font-heading text-sm sm:text-base text-[#B8972E] font-semibold tracking-wide mt-1">A Miracle is Real</p>
            </div>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#1A202C]">
              A place for families<br />where{" "}
              <span className="text-[#6A0DAD] italic miracle-glow">a miracle</span> is real.
            </h1>
            <p className="mt-6 text-lg text-[#4A5568] max-w-md leading-relaxed">
              A gentle, supportive community for parents of NICU babies. Chat in the forum,
              lift each other up on the help board, and share kindness with AMIR.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_miracle-babies-amir/artifacts/sw42xqze_1783056936411.png"
                alt="The Twin Adventurers — Little Lion Robe Adventures"
                className="w-28 h-28 rounded-2xl object-cover border-2 border-[#6A0DAD]/70 shadow-md shrink-0"
              />
              <Link
                to="/auth"
                data-testid="hero-join-btn"
                className="inline-flex items-center gap-2 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-7 py-3.5 font-heading font-semibold transition-colors"
              >
                Join the family <ArrowRight size={18} />
              </Link>
              <Link
                to="/forum"
                data-testid="hero-forum-btn"
                className="inline-flex items-center gap-2 bg-white border border-[#6A0DAD] text-[#1A202C] hover:shadow-md rounded-full px-7 py-3.5 font-heading font-semibold transition-all"
              >
                Explore the forum
              </Link>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="mb-4 text-center md:text-right">
              <p className="font-display text-xl md:text-2xl italic text-[#6A0DAD] leading-snug">
                "Be on your guard; stand firm in the faith; be courageous; be strong.
                Do everything in love."
              </p>
              <p className="font-heading text-xs uppercase tracking-widest text-[#B8972E] mt-1.5">1 Corinthians 16:13-14</p>
            </div>
            <div className="relative">
              <div className="absolute -inset-6 bg-[#6A0DAD]/12 rounded-[3rem] blur-2xl" />
              <img
                src={COVER}
                alt="Community mural of a little one blooming among flowers and birds"
                className="relative rounded-[2.5rem] w-full h-[420px] md:h-[520px] object-cover object-center shadow-xl border-4 border-[#6A0DAD]/70"
              />
              <div className="absolute -bottom-5 -left-4 bg-white rounded-2xl shadow-lg border border-[#6A0DAD] px-5 py-4 animate-float">
                <p className="font-display text-3xl text-[#D4AF37] leading-none">{stats?.members ?? "—"}</p>
                <p className="text-xs text-[#4A5568] font-heading mt-1">families & counting</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 -mt-6 mb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Families", value: stats?.members ?? "—" },
            { label: "Conversations", value: stats?.topics ?? "—" },
            { label: "Help posts", value: stats?.help_requests ?? "—" },
            { label: "AMIR shared", value: shared },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-[#6A0DAD] rounded-2xl p-6 text-center shadow-sm">
              <p className="font-display text-4xl text-[#6B8EAC]">{s.value}</p>
              <p className="text-sm text-[#4A5568] font-heading mt-1">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-center">
          <span
            data-testid="visit-tally"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#6A0DAD]/8 border border-[#6A0DAD]/30 text-[#6A0DAD] font-heading text-sm font-semibold"
          >
            <Eye size={15} />
            {visits == null ? "—" : visits.toLocaleString()} visits & counting
          </span>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A202C] mb-8">
          Everything a NICU family needs, in one warm place
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {features.map((f) => (
            <Link
              key={f.title}
              to={f.to}
              data-testid={`feature-${f.title.toLowerCase().replace(/ /g, "-")}`}
              className="group bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm hover:shadow-md transition-all duration-300"
            >
              <span className={`grid place-items-center w-12 h-12 rounded-xl ${f.tint} text-white mb-4`}>
                <f.icon size={22} />
              </span>
              <h3 className="font-heading text-lg font-semibold text-[#1A202C] flex items-center gap-2">
                {f.title}
                <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all text-[#6B8EAC]" />
              </h3>
              <p className="text-[#4A5568] mt-1.5 leading-relaxed">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured art from the gallery */}
      {art.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="font-heading text-xs uppercase tracking-widest text-[#B8972E] mb-1 flex items-center gap-2">
                <Sparkles size={14} /> From the Miracle Gallery
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A202C]">
                Art & joyful milestones from our families
              </h2>
            </div>
            <Link to="/gallery" data-testid="home-view-gallery" className="hidden sm:inline-flex items-center gap-2 text-[#6B8EAC] font-heading font-semibold">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {visibleArt.map((item, k) => (
              <Link key={`${item._id}-${artIdx}-${k}`} to="/gallery" data-testid={`home-art-${item._id}`}
                className="block aspect-square rounded-2xl overflow-hidden border border-[#6A0DAD] shadow-sm hover:shadow-md transition-all relative group animate-fade-up">
                {item.featured && (
                  <span className="absolute top-2 left-2 z-10 px-2.5 py-0.5 rounded-full bg-[#D4AF37] text-white text-[10px] font-heading font-semibold">Featured</span>
                )}
                <img src={galleryImg(item)} alt={item.caption || "artwork"} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
              </Link>
            ))}
          </div>
          {art.length > VISIBLE && (
            <div className="flex justify-center gap-1.5 mt-5">
              {art.map((item, i) => (
                <span key={item._id} className={`h-1.5 rounded-full transition-all ${i === artIdx ? "w-6 bg-[#6A0DAD]" : "w-1.5 bg-[#6A0DAD]/25"}`} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Meet the AMIR coin */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-heading text-xs uppercase tracking-widest text-[#B8972E] mb-1 flex items-center gap-2">
              <Coins size={14} /> The official coin
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A202C]">
              Meet AMIR — A Miracle is Real, on-chain
            </h2>
          </div>
        </div>
        <CoinCard />
      </section>

      {/* Wall of Support preview */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-heading text-xs uppercase tracking-widest text-[#B8972E] mb-1 flex items-center gap-2">
              <HeartHandshake size={14} /> Everyone is welcome
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A202C]">
              Wall of Support
            </h2>
            <p className="text-[#4A5568] mt-2 max-w-xl">You don't have to be a NICU family to care. Leave a note of love — no account needed.</p>
          </div>
          <Link to="/support" data-testid="home-view-support" className="hidden sm:inline-flex items-center gap-2 text-[#6A0DAD] font-heading font-semibold shrink-0">
            Leave a note <ArrowRight size={16} />
          </Link>
        </div>
        {support.length > 0 ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
            {support.map((m) => (
              <Link key={m._id} to="/support" className="break-inside-avoid block rounded-2xl border border-[#6A0DAD]/30 bg-[#6A0DAD]/6 p-5 hover:shadow-md transition-all">
                <Heart size={15} className="text-[#6A0DAD] mb-2" fill="#6A0DAD" />
                <p className="text-[#1A202C] leading-relaxed">{m.message}</p>
                <p className="text-xs text-[#4A5568] font-heading mt-3">— {m.name}</p>
              </Link>
            ))}
          </div>
        ) : (
          <Link to="/support" className="block bg-white border border-[#6A0DAD] rounded-2xl p-8 text-center text-[#4A5568] hover:shadow-md transition-all">
            Be the first to leave a note of support 💜
          </Link>
        )}
      </section>

      {/* Subtle inspiration story */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-24">
        <div className="bg-gradient-to-br from-[#6B8EAC]/8 to-[#D4AF37]/8 border border-[#6A0DAD] rounded-2xl p-8 md:p-12">
          <p className="font-heading text-xs uppercase tracking-widest text-[#B8972E] mb-3">Why we're here</p>
          <blockquote className="font-display text-2xl md:text-3xl leading-relaxed text-[#1A202C] max-w-3xl italic">
            "Our twin boys, King and Amir, arrived at just 24 weeks — tiny and fighting, on a trach and g-tube.
            Almost three now and thriving, they remind us every day that a miracle is real —
            and that no family should walk this road alone."
          </blockquote>
          <p className="mt-5 text-sm text-[#4A5568] font-heading">— The family behind AMIR</p>
        </div>
      </section>
    </div>
  );
}
